import { useRef, type ReactNode } from 'react';
import { Card, Pill } from '../../../components/ui';
import type { BannerAppearance, BannerTemplateBase } from '../../../lib/api';
import { DESIGNS, designLabel, templateEdited, type CardProps } from './defaults';

type Template = BannerAppearance['template'];
type DesignKey = Exclude<BannerTemplateBase, 'custom'>;

/**
 * The gallery of built-in designs. A tile is the structure, not the colours:
 * where the notice sits on the page, where the buttons are, what the
 * preferences layer looks like. Picking one sets template.base and drops any
 * edited markup; the editor and the file round trip live under the tiles.
 */

/** The page frame every drawing sits in: a header line and a few content lines. */
function Page({ dim, children }: { dim?: boolean; children: ReactNode }) {
  return (
    <svg className="ck-design-art" viewBox="0 0 120 80" aria-hidden="true" focusable="false">
      <rect x="0.5" y="0.5" width="119" height="79" rx="3" className="ck-art-page" />
      <line x1="0.5" y1="11" x2="119.5" y2="11" className="ck-art-faint" />
      <rect x="8" y="18" width="40" height="4" rx="1" className="ck-art-faint-fill" />
      <rect x="8" y="26" width="70" height="2.5" rx="1" className="ck-art-faint-fill" />
      <rect x="8" y="31" width="60" height="2.5" rx="1" className="ck-art-faint-fill" />
      <rect x="8" y="36" width="66" height="2.5" rx="1" className="ck-art-faint-fill" />
      {dim && <rect x="0.5" y="0.5" width="119" height="79" rx="3" className="ck-art-dim" />}
      {children}
    </svg>
  );
}

/** Accept all and Reject all: two rectangles of one size, one filled, one outlined. */
function Buttons({ x, y, w = 16, h = 8, stacked }: { x: number; y: number; w?: number; h?: number; stacked?: boolean }) {
  const second = stacked ? { x, y: y + h + 2 } : { x: x + w + 3, y };
  return (
    <>
      <rect x={x} y={y} width={w} height={h} rx="1.5" className="ck-art-fill" />
      <rect x={second.x} y={second.y} width={w} height={h} rx="1.5" className="ck-art-line" />
    </>
  );
}

function Lines({ x, y, widths, gap = 5 }: { x: number; y: number; widths: number[]; gap?: number }) {
  return <>{widths.map((w, i) => <rect key={i} x={x} y={y + i * gap} width={w} height="2.5" rx="1" className="ck-art-text" />)}</>;
}

/** A category row: a label line and a small switch. */
function Switches({ x, y, w, rows }: { x: number; y: number; w: number; rows: number }) {
  return (
    <>
      {Array.from({ length: rows }, (_, i) => (
        <g key={i}>
          <rect x={x} y={y + i * 7 + 1} width={w - 14} height="2.5" rx="1" className="ck-art-text" />
          <rect x={x + w - 10} y={y + i * 7} width="10" height="4.5" rx="2.25" className={i === 0 ? 'ck-art-fill' : 'ck-art-line'} />
        </g>
      ))}
    </>
  );
}

const ART: Record<DesignKey, () => JSX.Element> = {
  card: () => (
    <Page>
      <rect x="6" y="34" width="54" height="40" rx="2.5" className="ck-art-surface" />
      <rect x="6" y="34" width="8" height="40" className="ck-art-rail" />
      <Lines x={19} y={40} widths={[26, 34, 28]} />
      <Buttons x={19} y={60} />
    </Page>
  ),
  strip: () => (
    <Page>
      <rect x="0.5" y="60" width="119" height="19.5" className="ck-art-surface" />
      <Lines x={8} y={65} widths={[44, 34]} />
      <Buttons x={76} y={65} h={9} />
    </Page>
  ),
  wall: () => (
    <Page dim>
      <rect x="29" y="14" width="62" height="52" rx="2.5" className="ck-art-surface" />
      <Lines x={35} y={20} widths={[30, 46]} />
      <Switches x={35} y={32} w={50} rows={3} />
      <Buttons x={35} y={55} />
    </Page>
  ),
  sheet: () => (
    <Page dim>
      <rect x="78" y="0.5" width="41.5" height="79" className="ck-art-surface" />
      <Lines x={84} y={8} widths={[22, 30]} />
      <Switches x={84} y={20} w={30} rows={4} />
      <Buttons x={84} y={54} w={30} h={8} stacked />
    </Page>
  ),
  toast: () => (
    <Page>
      <rect x="70" y="52" width="44" height="22" rx="2.5" className="ck-art-surface" />
      <Lines x={75} y={57} widths={[30]} />
      <Buttons x={75} y={63} w={16} h={7} />
    </Page>
  ),
  brand: () => (
    <Page>
      <rect x="6" y="26" width="54" height="48" rx="2.5" className="ck-art-surface" />
      <rect x="6" y="26" width="54" height="11" className="ck-art-rail" />
      <rect x="11" y="29" width="16" height="5" rx="1" className="ck-art-fill" />
      <Lines x={11} y={42} widths={[36, 42]} />
      <Buttons x={11} y={54} w={44} h={7} stacked />
    </Page>
  ),
};

export default function DesignCard({ a, update, readOnly, issueCount, busy, onEdit, onReset, onExport, onImport }: CardProps & {
  /** Template issues on the current draft, shown as a count beside the design name. */
  issueCount: number;
  busy: 'export' | 'import' | null;
  onEdit: () => void;
  onReset: () => void;
  onExport: () => void;
  onImport: (file: File) => void;
}) {
  const t = a.template;
  const edited = templateEdited(t);
  const file = useRef<HTMLInputElement>(null);

  function pick(key: DesignKey) {
    if (readOnly) return;
    if (key === t.base && !edited) return;
    if (edited && !window.confirm('The edited design is dropped. Pick this design?')) return;
    update((x) => ({ ...x, template: { base: key, html: '', css: '', name: '' } as Template }));
  }

  return (
    <Card title="Design">
      <div className="ck-designs" role="radiogroup" aria-label="Design">
        {DESIGNS.map((d) => {
          const on = t.base === d.key;
          const Art = ART[d.key];
          return (
            <button
              key={d.key} type="button" role="radio" aria-checked={on}
              className={`ck-design${on ? ' on' : ''}`} disabled={readOnly}
              onClick={() => pick(d.key)}
            >
              <Art />
              <span className="ck-design-name">{d.name}</span>
              <span className="ck-design-desc">{d.description}</span>
            </button>
          );
        })}
      </div>

      <div className="ck-design-foot">
        <div className="ck-design-state">
          <strong>{designLabel(t)}</strong>
          {edited && t.base !== 'custom' && t.name.trim() && <span className="muted"> · edited from {DESIGNS.find((d) => d.key === t.base)?.name}</span>}
          {issueCount > 0 && <Pill tone="bad">{issueCount} {issueCount === 1 ? 'issue' : 'issues'}</Pill>}
        </div>
        <div className="ck-design-actions">
          <button className="small" onClick={onEdit} disabled={readOnly}>Edit the design</button>
          {edited && <button className="small" onClick={onReset} disabled={readOnly}>Reset to the built-in design</button>}
          <button className="small" onClick={onExport} disabled={busy !== null}>{busy === 'export' ? 'Exporting…' : 'Export'}</button>
          <button className="small" onClick={() => file.current?.click()} disabled={readOnly || busy !== null}>{busy === 'import' ? 'Importing…' : 'Import'}</button>
          <input
            ref={file} type="file" accept=".json,application/json" hidden aria-label="Import a banner file"
            onChange={(e) => { const f = e.target.files?.[0]; if (f) onImport(f); e.target.value = ''; }}
          />
        </div>
      </div>
    </Card>
  );
}
